import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:3001";

function getToken() {
  return localStorage.getItem("token") || localStorage.getItem("access_token") || "";
}

async function apiRequest(path, options = {}) {
  const token = getToken();

  const headers = {
    ...(options.headers || {}),
  };

  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = `Request failed: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage =
        errorData?.detail ||
        errorData?.message ||
        errorData?.error ||
        errorMessage;
    } catch (e) {
      // ignore parse errors
    }
    throw new Error(errorMessage);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  return response;
}

function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled = false,
  style = {},
}) {
  const styles = {
    primary: {
      background: "linear-gradient(135deg, #ea580c, #f97316)",
      color: "white",
      border: "none",
    },
    secondary: {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    ghost: {
      background: "white",
      color: "#334155",
      border: "1px solid #cbd5e1",
    },
    danger: {
      background: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fca5a5",
    },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "12px 16px",
        borderRadius: "14px",
        fontSize: "14px",
        fontWeight: "800",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "0.2s ease",
        ...styles[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Input({ label, ...props }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: "14px",
          fontWeight: "700",
          color: "#334155",
          marginBottom: "8px",
        }}
      >
        {label}
      </div>
      <input
        {...props}
        style={{
          width: "100%",
          padding: "14px 16px",
          borderRadius: "14px",
          border: "1px solid #cbd5e1",
          outline: "none",
          fontSize: "15px",
          ...props.style,
        }}
      />
    </label>
  );
}

function Select({ label, children, ...props }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: "14px",
          fontWeight: "700",
          color: "#334155",
          marginBottom: "8px",
        }}
      >
        {label}
      </div>
      <select
        {...props}
        style={{
          width: "100%",
          padding: "14px 16px",
          borderRadius: "14px",
          border: "1px solid #cbd5e1",
          outline: "none",
          fontSize: "15px",
          background: "white",
          ...props.style,
        }}
      >
        {children}
      </select>
    </label>
  );
}

export default function Disputes() {
  const location = useLocation();
  const queryClientId = new URLSearchParams(location.search).get("client_id"); 
  
  const [disputes, setDisputes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    client_id: "",
    creditor: "",
    account_number: "",
    reason: "",
    action: "delete",
    status: "open",
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [disputesData, clientsData] = await Promise.all([
        apiRequest("/disputes"),
        apiRequest("/clients"),
      ]);

      setDisputes(Array.isArray(disputesData) ? disputesData : []);
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (err) {
      setError(err.message || "Failed to load disputes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleCreateDispute(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await apiRequest("/disputes", {
        method: "POST",
        body: JSON.stringify({
          client_id: Number(form.client_id),
          creditor: form.creditor,
          account_number: form.account_number,
          reason: form.reason,
          action: form.action,
          status: form.status,
        }),
      });

      setForm({
        client_id: "",
        creditor: "",
        account_number: "",
        reason: "",
        action: "delete",
        status: "open",
      });

      setSuccess("Dispute created successfully.");
      await loadData();
    } catch (err) {
      setError(err.message || "Failed to create dispute.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteDispute(disputeId) {
    const confirmed = window.confirm("Delete this dispute?");
    if (!confirmed) return;

    try {
      setError("");
      setSuccess("");

      await apiRequest(`/disputes/${disputeId}`, {
        method: "DELETE",
      });

      setSuccess("Dispute deleted.");
      await loadData();
    } catch (err) {
      setError(err.message || "Failed to delete dispute.");
    }
  }

  function getClientName(clientId) {
    const match = clients.find((client) => {
      const id = client.id ?? client.client_id ?? client.clientId;
      return String(id) === String(clientId);
    });

    return match?.full_name || match?.name || `Client #${clientId}`;
  }
  const visibleDisputes = queryClientId
    ? disputes.filter((dispute) => String(dispute.client_id) === String(queryClientId))
    : disputes;
  return (
    <div
      style={{
        padding: "24px",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{ marginBottom: "24px" }}>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#64748b",
              marginBottom: "6px",
            }}
          >
            Integrity Suite CRM
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: "64px",
              lineHeight: 1,
              fontWeight: "900",
              color: "#0f172a",
            }}
          >
            Disputes
          </h1>
        </div>

        {error ? (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              padding: "16px 18px",
              borderRadius: "18px",
              marginBottom: "18px",
              fontWeight: "700",
            }}
          >
            {error}
          </div>
        ) : null}

        {success ? (
          <div
            style={{
              background: "#ecfdf5",
              border: "1px solid #86efac",
              color: "#166534",
              padding: "16px 18px",
              borderRadius: "18px",
              marginBottom: "18px",
              fontWeight: "700",
            }}
          >
            {success}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "420px 1fr",
            gap: "20px",
            alignItems: "start",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "24px",
              padding: "24px",
              boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
              border: "1px solid #e2e8f0",
            }}
          >
            <h2
              style={{
                marginTop: 0,
                marginBottom: "18px",
                fontSize: "28px",
                fontWeight: "800",
                color: "#0f172a",
              }}
            >
              Add Dispute
            </h2>

            <form onSubmit={handleCreateDispute} style={{ display: "grid", gap: "14px" }}>
              <Select
                label="Client"
                value={form.client_id}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, client_id: e.target.value }))
                }
                required
              >
                <option value="">Select client</option>
                {clients.map((client) => {
                  const clientId = client.id ?? client.client_id ?? client.clientId;
                  const clientName = client.full_name ?? client.name ?? `Client 
#${clientId}`;
                  return (
                    <option key={clientId} value={clientId}>
                      {clientName}
                    </option>
                  );
                })}
              </Select>

              <Select
                label="Credit Bureau / Creditor"
                value={form.creditor}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, creditor: e.target.value }))
                }
                required
              >
                <option value="">Select bureau</option>
                <option value="Experian">Experian</option>
                <option value="Equifax">Equifax</option>
                <option value="TransUnion">TransUnion</option>
                <option value="Other">Other</option>
              </Select>

              <Input
                label="Account Number"
                value={form.account_number}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, account_number: e.target.value }))
                }
              />

              <Select
                label="Action"
                value={form.action}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, action: e.target.value }))
                }
              >
                <option value="delete">Delete</option>
                <option value="correct">Correct</option>
                <option value="update">Update</option>
                <option value="validate">Validate</option>
              </Select>

              <Select
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </Select>

              <label style={{ display: "block" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#334155",
                    marginBottom: "8px",
                  }}
                >
                  Reason
                </div>
                <textarea
                  value={form.reason}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  required
                  style={{
                    width: "100%",
                    minHeight: "140px",
                    padding: "14px 16px",
                    borderRadius: "14px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    fontSize: "15px",
                    resize: "vertical",
                  }}
                />
              </label>

              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Create Dispute"}
              </Button>
            </form>
          </div>

          <div
            style={{
              background: "white",
              borderRadius: "24px",
              padding: "24px",
              boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                marginBottom: "18px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "28px",
                  fontWeight: "800",
                  color: "#0f172a",
                }}
              >
                Dispute List
              </h2>

              <Button variant="ghost" onClick={loadData}>
                Refresh
              </Button>
            </div>

            {loading ? (
              <div
                style={{
                  padding: "24px",
                  borderRadius: "18px",
                  background: "#f8fafc",
                  color: "#475569",
                  fontWeight: "700",
                }}
              >
                Loading disputes...
              </div>
            ) : visibleDisputes.length === 0 ? (
              <div
                style={{
                  padding: "24px",
                  borderRadius: "18px",
                  background: "#f8fafc",
                  color: "#64748b",
                  fontWeight: "700",
                }}
              >
                No disputes yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: "14px" }}>
                {visibleDisputes.map((dispute) => (
                  <div
                    key={dispute.id}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "18px",
                      padding: "18px",
                      background: "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "16px",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "22px",
                            fontWeight: "800",
                            color: "#0f172a",
                            marginBottom: "8px",
                          }}
                        >
                          {dispute.creditor || "Unknown Creditor"}
                        </div>

                        <div
                          style={{
                            fontSize: "15px",
                            color: "#64748b",
                            lineHeight: 1.7,
                          }}
                        >
                          <div>Client: {getClientName(dispute.client_id)}</div>
                          <div>Account #: {dispute.account_number || "—"}</div>
                          <div>Action: {dispute.action || "—"}</div>
                          <div>Status: {dispute.status || "—"}</div>
                        </div>

                        <div
                          style={{
                            marginTop: "12px",
                            padding: "14px",
                            borderRadius: "14px",
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            color: "#334155",
                            lineHeight: 1.6,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {dispute.reason || "No reason provided."}
                        </div>
                      </div>

                      <Button
                        variant="danger"
                        onClick={() => handleDeleteDispute(dispute.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
